"use client"

import React, { useEffect, useMemo, useState } from "react"

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

import { DynamicFormRenderer } from "@/components/dynamic-form"
import { getCSRFToken } from "@/lib/utils"

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type IntegrationTree = Array<Record<string, Record<string, string>>>

interface NormalizedIntegration {
	name: string
	actions: {
		action: string
		url: string
	}[]
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function normalizeIntegrations(input: IntegrationTree): NormalizedIntegration[] {
	return input.map((item) => {
		const name = Object.keys(item)[0]
		const actions = item[name] ?? {}
		return {
			name,
			actions: Object.entries(actions).map(([action, url]) => ({
				action,
				url,
			})),
		}
	})
}

function titleize(value: string) {
	return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

interface IntegrationActionDialogProps {
	open: boolean
	auto: boolean
	dc_id?: number
	onOpenChange: (open: boolean) => void
	trigger: React.ReactNode
	integrations: IntegrationTree
	onSuccess?: () => void
}

export function ActionDialog({
	open,
	onOpenChange,
	dc_id,
	auto,
	trigger,
	integrations,
	onSuccess,
}: IntegrationActionDialogProps) {
	const normalized = useMemo(() => normalizeIntegrations(integrations), [integrations])

	const [integrationName, setIntegrationName] = useState<string>("")
	const [actionUrl, setActionUrl] = useState<string | null>(null)

	const [schema, setSchema] = useState<any | null>(null)
	const [loadingSchema, setLoadingSchema] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const selectedIntegration = normalized.find((i) => i.name === integrationName)

	/* ------------------------------------------------------------------ */
	/* Fetch schema when action selected                                   */
	/* ------------------------------------------------------------------ */

	useEffect(() => {
		if (!actionUrl) return

		let cancelled = false

		async function fetchSchema() {
			setLoadingSchema(true)
			setError(null)
			setSchema(null)

			try {
				const res = await fetch(actionUrl, { method: "GET" })
				if (!res.ok) throw new Error(`Schema fetch failed (${res.status})`)
				const json = await res.json()
				if (!cancelled) setSchema(json)
			} catch (err: any) {
				if (!cancelled) setError(err.message ?? "Failed to load schema")
			} finally {
				if (!cancelled) setLoadingSchema(false)
			}
		}

		fetchSchema()
		return () => {
			cancelled = true
		}
	}, [actionUrl])

	/* ------------------------------------------------------------------ */
	/* Submit handler                                                      */
	/* ------------------------------------------------------------------ */

	async function handleSubmit(data: Record<string, any>) {
		console.log("Submitting data:", data)
		if (!schema?.form?.action) {
			setError("Invalid schema: missing form action")
			return
		}

		setSubmitting(true)
		setError(null)


		try {
			const csrfToken = getCSRFToken()
			const res = await fetch(schema.form.action, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-CSRF-Token": csrfToken || ""
				},
				body: JSON.stringify(data),
			})

			if (!res.ok) {
				const text = await res.text()
				throw new Error(text || "Submission failed")
			}

			const res_data = await res.json()

			const req_data = {
				name: data.integrationName,
				model_name: res_data.model_name,
				model_data: res_data.model_data,
				document_classifier_id: dc_id,
				auto: auto
			}


			const int_res = await fetch("/integrations", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-CSRF-Token": csrfToken || ""
				},
				body: JSON.stringify(req_data),
			})


			if (!int_res.ok) {
				const text = await int_res.text()
				throw new Error(text || "Integration creation failed")
			}
			const int_res_data = await int_res.json()
			console.log("Integration created:", int_res_data)

			onOpenChange(false)

			// reset dialog state
			setIntegrationName("")
			setActionUrl(null)
			setSchema(null)
		} catch (err: any) {
			setError(err.message ?? "Failed to submit")
		} finally {
			setSubmitting(false)
		}
	}

	/* ------------------------------------------------------------------ */
	/* Render                                                              */
	/* ------------------------------------------------------------------ */

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>{trigger}</DialogTrigger>

			<DialogContent className="max-w-3xl">
				<DialogHeader>
					<DialogTitle>Add Integration Action</DialogTitle>
					<DialogDescription>
						Choose an integration, then an action. Configuration is loaded dynamically.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">

					{/* Integration */}
					<div>
						<Select
							value={integrationName}
							onValueChange={(value) => {
								setIntegrationName(value)
								setActionUrl(null)
								setSchema(null)
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select integration" />
							</SelectTrigger>
							<SelectContent>
								{normalized.map((i) => (
									<SelectItem key={i.name} value={i.name}>
										{i.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Action */}
					{selectedIntegration && (
						<div>
							<Select
								onValueChange={(url) => {
									setActionUrl(url)
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select action" />
								</SelectTrigger>
								<SelectContent>
									{selectedIntegration.actions.map((a) => (
										<SelectItem key={a.url} value={a.url}>
											{titleize(a.action)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{/* State */}
					{loadingSchema && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Spinner />
							Loading configuration…
						</div>
					)}

					{error && (
						<div className="rounded-md bg-destructive/10 text-destructive px-3 py-2 text-sm">
							{error}
						</div>
					)}

					{/* Dynamic Form */}
					{schema && !loadingSchema && (
						<DynamicFormRenderer
							schema={schema}
							onSubmit={handleSubmit}
							disabled={submitting}
						/>
					)}

				</div>

				<DialogFooter>
					<Button
						variant="secondary"
						onClick={() => onOpenChange(false)}
						disabled={submitting}
					>
						Cancel
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

